
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Property } from '../types';
import { MapPin, BedDouble, Bath, Image as ImageIcon, Sparkles } from 'lucide-react';
import { fetchImagesForDeal } from '../services/ghlService';

interface PropertyCardProps {
  property: Property;
}

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const [displayImage, setDisplayImage] = useState<string | null>(property.imageUrl || null);
  const [loadingImage, setLoadingImage] = useState(!property.imageUrl);

  useEffect(() => {
    if (!property.imageUrl && property.listingRef) {
      const getImages = async () => {
        const urls = await fetchImagesForDeal(property.listingRef);
        if (urls.length > 0) {
          setDisplayImage(urls[0]);
        }
        setLoadingImage(false);
      };
      getImages();
    }
  }, [property.imageUrl, property.listingRef]);

  const formattedPrice = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(property.price);

  const formattedMarketValue = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(property.marketValue || 0);

  const profitValue = (property.marketValue || 0) - property.price;
  const formattedProfit = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0
  }).format(profitValue);

  return (
    <Link
      to={`/property/${property.id}`}
      className="group block bg-white rounded-[2rem] overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 border border-slate-100"
    >
      {/* Image Container */}
      <div className="relative h-72 overflow-hidden bg-slate-50 flex items-center justify-center">
        {loadingImage ? (
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-[3px] border-slate-100 border-t-indigo-600 rounded-full animate-spin" />
            <span className="text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">Sourcing Media</span>
          </div>
        ) : displayImage ? (
          <img
            src={displayImage}
            alt={property.hook}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <img
            src="https://assets.sitefindr.co.uk/deals/placeholders/Gemini_Generated_Image_dx19pidx19pidx19.png"
            alt="Property Awaiting Media"
            className="w-full h-full object-cover opacity-60 transition-opacity group-hover:opacity-100"
          />
        )}

        <div className="absolute top-5 left-5">
          <span className="bg-white/95 backdrop-blur-md px-4 py-1.5 rounded-xl text-[9px] font-black text-slate-900 shadow-sm border border-slate-200 uppercase tracking-widest">
            {property.dealType}
          </span>
        </div>

        {property.yieldPercent > 0 && (
          <div className="absolute top-5 right-5">
            <div className="bg-emerald-500/90 backdrop-blur-md px-3 py-1.5 rounded-xl text-white flex items-center gap-1.5 shadow-lg">
              <Sparkles size={12} fill="white" />
              <span className="text-[10px] font-black uppercase tracking-widest">{property.yieldPercent}% Yield</span>
            </div>
          </div>
        )}

        <div className="absolute bottom-5 left-5 right-5">
          <div className="bg-white/95 backdrop-blur-xl p-4 rounded-2xl shadow-xl border border-white/20 group-hover:bg-indigo-600 transition-all duration-300">
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-col">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest group-hover:text-indigo-100 transition-colors">Deal Price</span>
                <span className="text-sm font-black text-slate-900 group-hover:text-white transition-colors">{formattedPrice}</span>
              </div>
              <div className="w-px h-8 bg-slate-100 group-hover:bg-indigo-500/50 transition-colors" />
              <div className="flex flex-col items-center">
                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest group-hover:text-indigo-100 transition-colors">End Value</span>
                <span className="text-sm font-black text-slate-900 group-hover:text-white transition-colors">{formattedMarketValue}</span>
              </div>
              <div className="w-px h-8 bg-slate-100 group-hover:bg-indigo-500/50 transition-colors" />
              <div className="flex flex-col items-end">
                <span className="text-[8px] font-black text-indigo-600 uppercase tracking-widest group-hover:text-indigo-100 transition-colors">Difference</span>
                <span className="text-sm font-black text-indigo-600 group-hover:text-white transition-colors">{formattedProfit}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-8">
        <div className="flex items-center gap-2 text-slate-400 text-[10px] mb-4 font-black uppercase tracking-[0.2em]">
          <MapPin size={14} className="text-indigo-500" />
          <span>{property.town} • {property.postcodeOutcode}</span>
        </div>

        <h3 className="text-xl font-black text-slate-900 mb-8 leading-[1.3] group-hover:text-indigo-600 transition-colors line-clamp-2 min-h-[3.25rem]">
          {property.hook}
        </h3>

        <div className="flex items-center gap-6 pt-6 border-t border-slate-50">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-slate-50 rounded-lg text-slate-400">
              <BedDouble size={16} />
            </div>
            <span className="text-sm font-black text-slate-700">{property.bedrooms} <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Beds</span></span>
          </div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-slate-50 rounded-lg text-slate-400">
              <Bath size={16} />
            </div>
            <span className="text-sm font-black text-slate-700">{property.bathrooms} <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Baths</span></span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
